import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { isInRect } from '../../util/DOMUtil';
import { KeyCode } from '../../util/Constants';
import { getClientXY } from '../../util/EventUtil';
import { isTouchDevice } from '../../util/ScreenUtil';

import './TwoPointResizable.css';

const debug = require('../../debug/debug')('wc:svg:TwoPointResizable');

const TwoPointResizable = (getPoints) => (Component) =>
  class TwoPointResizable extends React.Component {
    static WrappedComponent = Component;

    static propTypes = {
      data: PropTypes.object,
      isActive: PropTypes.bool,
      svgNode: PropTypes.object,
      onUpdateShape: PropTypes.func,
      onResize: PropTypes.func,
      onDragStop: PropTypes.func
    };

    static defaultProps = {
      onResize: _.noop,
      onDragStop: _.noop
    };

    constructor(props) {
      super(props);
      this._dragEvent = null;
      this.state = getPoints(props);
    }

    componentDidUpdate(prevProps) {
      if (!_.isEqual(this.props.data, prevProps.data)) {
        this.setState(getPoints(this.props));
      }
    }

    componentWillUnmount() {
      this.removeListeners();
    }

    render() {
      return <Component {...this.props} renderResizeNodes={this.renderResizeNodes} />;
    }

    renderResizeNodes = (children) => {
      const { stroke, strokeWidth } = this.props.data;
      const { x1, y1, x2, y2 } = this.state;

      const r = isTouchDevice() ? 8 : 4;

      const dotProps = {
        fill: '#fff',
        stroke: '#000',
        onMouseDown: this.onResizeStart,
        onTouchStart: this.onResizeStart
      };

      const dot1Props = { ...dotProps, className: 'dot dot-1', r, cx: x1, cy: y1 };
      const dot2Props = { ...dotProps, className: 'dot dot-2', r, cx: x2, cy: y2 };

      const lineProps = {
        ...this.state,
        stroke,
        strokeWidth,
        strokeOpacity: 0.3
      };

      return (
        <g name="TwoPointResizable">
          {children}
          <circle {...dot1Props} />
          <circle {...dot2Props} />
          {this._dragEvent ? <line {...lineProps} /> : null}
        </g>
      );
    };

    addListeners = () => {
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('touchmove', this.onResize, { passive: false });
      document.addEventListener('mouseup', this.onResizeStop, true);
      document.addEventListener('touchend', this.onResizeStop, true);
      document.addEventListener('keydown', this.onKeyDown);
    };

    removeListeners = () => {
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('touchmove', this.onResize);
      document.removeEventListener('mouseup', this.onResizeStop, true);
      document.removeEventListener('touchend', this.onResizeStop, true);
      document.removeEventListener('keydown', this.onKeyDown);
    };

    onResizeStart = (e) => {
      const num = parseInt(e.target.getAttribute('class').replace('dot dot-', ''), 10);
      const [clientX, clientY] = getClientXY(e);
      this._dragEvent = {
        prevX: clientX,
        prevY: clientY,
        point: num
      };

      this.addListeners();
    };

    onResize = (e) => {
      e.preventDefault();
      const { x1, y1, x2, y2 } = this.state;
      const [clientX, clientY] = getClientXY(e);
      if (!this._dragEvent) {
        return;
      }

      const { prevX, prevY, point } = this._dragEvent;

      const diffX = clientX - prevX;
      const diffY = clientY - prevY;

      const nextState = {};
      if (point === 1) {
        nextState.x1 = x1 + diffX;
        nextState.y1 = y1 + diffY;
      } else {
        nextState.x2 = x2 + diffX;
        nextState.y2 = y2 + diffY;
      }

      this._dragEvent.prevX = clientX;
      this._dragEvent.prevY = clientY;

      this.setState(nextState);
      this.props.onResize({ ...this.props.data, ...nextState });
    };

    onResizeStop = async (e) => {
      e.stopPropagation();
      const { svgNode, onUpdateShape, onDragStop } = this.props;
      const [clientX, clientY] = getClientXY(e);
      if (!isInRect(svgNode.getBoundingClientRect(), clientX, clientY)) {
        return this.cancelResizeEvent();
      }

      this.removeListeners();
      this._dragEvent = null;

      const data = {
        ...this.props.data,
        ...this.state
      };

      onDragStop();

      if (_.isEqual(data, this.props.data)) {
        debug('[onResizeStop()] no change in data. return.');
        return;
      }

      debug('[onResizeStop()] data: ', data);
      try {
        await onUpdateShape(data);
      } catch (err) {
        debug('[onResizeStop()] error, resetting state ', err);
        this.setState(getPoints(this.props));
      }
    };

    onKeyDown = (e) => {
      if (e.keyCode === KeyCode.ESCAPE && this._dragEvent) {
        e.stopPropagation();
        this.cancelResizeEvent();
      }
    };

    cancelResizeEvent = () => {
      this._dragEvent = null;
      this.removeListeners();
      this.setState(getPoints(this.props));
    };
  };

export default TwoPointResizable;
